import {
  Dialog,
  DialogContent,
  Grid,
  Tab,
  Tabs
} from '@material-ui/core';
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { SiteContext, UserContext } from '../../../contexts';
import * as DTO from '../../../dto';
import { useUserPreferences, useUpdateUserPreferences } from '../../../hooks';
import { DialogTitle, LanguageString } from '../../Common';
import { DynamicForm } from '../../DynamicForm';
import { CaseLoadColumnSettingsTab } from './CaseLoadColumnSettingsTab';
import { CaseLoadAdvisorTab } from './CaseLoadAdvisorTab';

interface CaseLoadFilterDialogProps {
  open: boolean;
  toggle: () => void;
  selectedAdvisorIds: number[];
  changedSelectedAdvisorIds: (advisorIds: number[]) => void;
  showClosedUsers: boolean;
  changedShowClosedUsers: (showClosedUsers: boolean) => void;
}

export const CaseLoadFilterDialog: React.FC<CaseLoadFilterDialogProps> = props => {
  const [currentTab, setCurrentTab] = useState(0);
  const [dismissOnSave, setDismissOnSave] = useState(false);
  const siteCtx = useContext(SiteContext);
  const userCtx = useContext(UserContext);
  const userPreferences = useUserPreferences({
    variables: {
      userId: userCtx.user.id,
      keys: [DTO.PreferenceKey.CaseLoadFilters]
    },
    skip: !userCtx.user.id
  });
  const [updateUserPreferences] = useUpdateUserPreferences();
  let dynamicFormRef: DynamicForm | null = null;
  const [innerAdvisorIds, setInnerAdvisorIds] = useState<number[]>(props.selectedAdvisorIds);
  const filters = useMemo(() => {
    if (userPreferences.data?.userPreference?.length) {
      const filtersEntry = userPreferences.data.userPreference.find(entry => entry.key === DTO.PreferenceKey.CaseLoadFilters);
      if (filtersEntry?.value) {
        return JSON.parse(filtersEntry.value);
      }
    }

    return {};
  }, [userPreferences.data]);
  useEffect(() => setInnerAdvisorIds(props.selectedAdvisorIds), [props.selectedAdvisorIds]);

  const toggle = () => {
    if (currentTab === 1 && dynamicFormRef) {
      setDismissOnSave(true);
      void dynamicFormRef.submitForm();
    } else {
      props.toggle();
    }
    props.changedSelectedAdvisorIds(innerAdvisorIds);
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    if (currentTab === 1 && dynamicFormRef) {
      void dynamicFormRef.submitForm();
    }

    setCurrentTab(newValue);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={props.open} onClose={toggle} aria-labelledby="Case Load Filters">
      <DialogTitle onClose={toggle}>
        <LanguageString groupName="CASE_LOAD" resourceName="CASE_LOAD_FILTERS" defaultText="Filters" />
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          aria-label="Filter Tabs"
          variant="fullWidth"
        >
          <Tab label={<LanguageString groupName="CASE_LOAD" resourceName="ADVISOR_FILTERS" defaultText="Advisors" />} />
          <Tab label={<LanguageString groupName="CASE_LOAD" resourceName="FILTERS" defaultText="Filters" />} />
          <Tab label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMNS" defaultText="Columns" />} />
        </Tabs>

        <Grid container>
          {currentTab === 0 && (
            <CaseLoadAdvisorTab selectedAdvisorIds={innerAdvisorIds} changedSelectedAdvisorIds={setInnerAdvisorIds} />
          )}
          {currentTab === 1 && (
            <Grid item xs>
              <Grid container>
                <Grid item xs={12}>
                  <DynamicForm
                    ref={f => (dynamicFormRef = f)}
                    formId={siteCtx.site?.caseLoadSettings?.filterFormId ?? 0}
                    autoloadFormSubmission={false}
                    showSubmitButton={false}
                    autoSave={true}
                    overrideFormSubmissionInput={Object.keys(filters).length > 0 ? (filters as DTO.SubmitFormInput) : undefined}
                    overrideSubmitHandler={async (input, form) => {
                      if (!filters || filters !== { filters: input }) {
                        await updateUserPreferences({
                          variables: {
                            input: [{ key: DTO.PreferenceKey.CaseLoadFilters, value: JSON.stringify(input) }]
                          },
                          refetchQueries: ['GetUserPreferences']
                        });
                      }
                      if (dismissOnSave) {
                        setDismissOnSave(false);
                        props.toggle();
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {currentTab === 2 && <CaseLoadColumnSettingsTab />}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CaseLoadFilterDialog;
